import { DateTimeUtils } from "@stellar/utils";
import { LocalStorageUtils } from "@utils/local-storage-utils";

/** Local storage key used to persist the analytics feedback survey state. */
const ANALYTICS_KEY = "analytics-feedback";

/**
 * How long the survey stays hidden after a dismissal without feedback. Once it
 * elapses, the survey is shown again until the user provides feedback or opts
 * out permanently.
 */
const DISMISS_COOLDOWN_MINUTES = 30;
const DISMISS_COOLDOWN_MS =
  DISMISS_COOLDOWN_MINUTES * DateTimeUtils.MILLISECONDS_IN_MINUTE;

/** Persisted state of the in-house premium analytics feedback survey. */
interface AnalyticsFeedbackStorageState {
  /** Whether the user has already submitted feedback. */
  hasProvidedFeedback: boolean;

  /**
   * Epoch timestamp (ms) of the last dismissal without feedback, or `null` if
   * it was never dismissed.
   */
  lastDismissedAt: number | null;

  /** Whether the user explicitly opted out of ever seeing the survey again. */
  hasDismissedPermanently: boolean;
}

/** Reads the persisted survey state, falling back to a pristine default. */
function getState(): AnalyticsFeedbackStorageState {
  return (
    LocalStorageUtils.getJSONItem<AnalyticsFeedbackStorageState>(
      ANALYTICS_KEY
    ) ?? {
      hasProvidedFeedback: false,
      lastDismissedAt: null,
      hasDismissedPermanently: false,
    }
  );
}

/**
 * Whether the feedback survey should be shown to the user. It is hidden once
 * the user provided feedback or opted out permanently, and stays hidden for a
 * cool down period after each dismissal without feedback.
 */
export function shouldShowAnalyticsFeedback(): boolean {
  const { hasProvidedFeedback, lastDismissedAt, hasDismissedPermanently } =
    getState();

  if (hasProvidedFeedback || hasDismissedPermanently) {
    return false;
  }

  if (lastDismissedAt === null) {
    return true;
  }

  return Date.now() - lastDismissedAt >= DISMISS_COOLDOWN_MS;
}

/** Marks the survey as completed so it is never shown again. */
export function markAnalyticsFeedbackProvided(): void {
  LocalStorageUtils.setJSONItem(ANALYTICS_KEY, {
    ...getState(),
    hasProvidedFeedback: true,
  });
}

/** Records the current time as the last dismissal without provided feedback. */
export function recordAnalyticsFeedbackDismissal(): void {
  LocalStorageUtils.setJSONItem(ANALYTICS_KEY, {
    ...getState(),
    lastDismissedAt: Date.now(),
  });
}

/** Marks the survey as permanently dismissed so it is never shown again. */
export function dismissAnalyticsFeedbackPermanently(): void {
  LocalStorageUtils.setJSONItem(ANALYTICS_KEY, {
    ...getState(),
    hasDismissedPermanently: true,
  });
}
